import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import styles from "./GlobalTooltip.module.css";

type TooltipState = {
  texto: string;
  alvo: HTMLElement;
};

type Posicao = {
  left: number;
  top: number;
  arrowLeft: number;
  side: "top" | "bottom";
};

const MARGEM_TELA = 8;
const ESPACO_ALVO = 10;

function elementoTooltip(origem: EventTarget | null): HTMLElement | null {
  if (!(origem instanceof Element)) return null;
  if (origem.closest("input, textarea, select, [contenteditable='true']")) return null;
  const el = origem.closest<HTMLElement>(".tooltip[data-tooltip]");
  const texto = el?.dataset.tooltip?.trim();
  if (!el || !texto) return null;
  return el;
}

function calcularPosicao(alvo: HTMLElement, tooltip: HTMLDivElement): Posicao {
  const rect = alvo.getBoundingClientRect();
  const largura = tooltip.offsetWidth;
  const altura = tooltip.offsetHeight;
  const centroAlvo = rect.left + rect.width / 2;
  const left = Math.min(
    Math.max(centroAlvo - largura / 2, MARGEM_TELA),
    window.innerWidth - largura - MARGEM_TELA
  );
  const cabeAbaixo = rect.bottom + ESPACO_ALVO + altura <= window.innerHeight - MARGEM_TELA;
  const side: Posicao["side"] = cabeAbaixo ? "bottom" : "top";
  const top = side === "bottom" ? rect.bottom + ESPACO_ALVO : Math.max(MARGEM_TELA, rect.top - ESPACO_ALVO - altura);
  const arrowLeft = Math.min(Math.max(centroAlvo - left, 12), largura - 12);

  return { left, top, arrowLeft, side };
}

export function GlobalTooltip() {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [estado, setEstado] = useState<TooltipState | null>(null);
  const [posicao, setPosicao] = useState<Posicao | null>(null);

  useEffect(() => {
    function mostrar(evento: PointerEvent | FocusEvent) {
      const alvo = elementoTooltip(evento.target);
      if (!alvo) {
        setEstado(null);
        setPosicao(null);
        return;
      }
      setEstado({ texto: alvo.dataset.tooltip?.trim() ?? "", alvo });
    }

    function esconder(evento: PointerEvent | FocusEvent) {
      const alvo = elementoTooltip(evento.target);
      if (!alvo || alvo !== estado?.alvo) return;
      const proximo = evento.relatedTarget;
      if (proximo instanceof Node && alvo.contains(proximo)) return;
      setEstado(null);
      setPosicao(null);
    }

    document.addEventListener("pointerover", mostrar, true);
    document.addEventListener("focusin", mostrar, true);
    document.addEventListener("pointerout", esconder, true);
    document.addEventListener("focusout", esconder, true);

    return () => {
      document.removeEventListener("pointerover", mostrar, true);
      document.removeEventListener("focusin", mostrar, true);
      document.removeEventListener("pointerout", esconder, true);
      document.removeEventListener("focusout", esconder, true);
    };
  }, [estado?.alvo]);

  useLayoutEffect(() => {
    if (!estado || !tooltipRef.current) return;

    const atualizar = () => {
      if (!document.body.contains(estado.alvo) || !tooltipRef.current) {
        setEstado(null);
        setPosicao(null);
        return;
      }
      setPosicao(calcularPosicao(estado.alvo, tooltipRef.current));
    };

    atualizar();
    window.addEventListener("resize", atualizar);
    window.addEventListener("scroll", atualizar, true);
    return () => {
      window.removeEventListener("resize", atualizar);
      window.removeEventListener("scroll", atualizar, true);
    };
  }, [estado]);

  if (!estado) return null;

  return createPortal(
    <div
      ref={tooltipRef}
      className={`${styles.tooltip} ${posicao?.side === "top" ? styles.top : styles.bottom}`}
      style={{
        left: posicao ? `${posicao.left}px` : 0,
        top: posicao ? `${posicao.top}px` : 0,
        opacity: posicao ? 1 : 0,
        "--tooltip-arrow-left": posicao ? `${posicao.arrowLeft}px` : "50%",
      } as CSSProperties}
      role="tooltip"
    >
      {estado.texto}
    </div>,
    document.body
  );
}
